/**
 * A donor's year-end letter (migration 0084; N6 of the #55 plan, §B.17.5),
 * printed and sent by the operator, on the earnings statement's pattern.
 *
 * WHAT IT LISTS is the API's (apps/api/src/services/donors.ts): every gift
 * received in the year, on the day it arrived. A pledge made is not a gift
 * and is not here; a pledge paid is, as cash. An in-kind gift is described and
 * never valued, since the donor values it (Publication 1771). An event gift
 * says what the donor received in return and what was deductible.
 *
 * The wording is a starting point, and says so on screen only: the note to the
 * CPA is `.no-print`, so the printed letter carries none of it.
 */

import { useEffect, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { ApiError, api, type DonorLetter, type GiftKind } from '../api.js';
import { formatCents } from '../lib/money.js';
import { settingsTab } from '../lib/settings-tabs.js';

const KIND_LABELS: Record<GiftKind, string> = { cash: 'Cash', in_kind: 'In-Kind', event: 'Event' };

export function DonorLetterPage() {
  const { partyId } = useParams();
  const [searchParams] = useSearchParams();
  const year = Number(searchParams.get('year') ?? new Date().getFullYear() - 1);
  const [letter, setLetter] = useState<DonorLetter | null>(null);
  const [error, setError] = useState<{ message: string; details: boolean } | null>(null);

  useEffect(() => {
    let live = true;
    api.donorLetter(partyId!, year).then(
      (result) => {
        if (live) setLetter(result);
      },
      (caught: unknown) => {
        if (!live) return;
        setError({
          message: caught instanceof ApiError ? caught.message : 'Could not build the letter.',
          details: caught instanceof ApiError && caught.code === 'business_details_missing',
        });
      },
    );
    return () => {
      live = false;
    };
  }, [partyId, year]);

  return (
    <section className="earnings-statement donor-letter">
      <p className="no-print">
        <Link to={`/reports?report=giving&from=${year}-01-01&to=${year}-12-31`}>Back to Giving</Link>
      </p>
      <div className="row statement-head no-print">
        <h1>Year-End Letter, {year}</h1>
        {letter && (
          <button type="button" onClick={() => window.print()}>
            Print Letter
          </button>
        )}
      </div>

      {error && (
        <p role="alert" className="error">
          {error.message} {error.details && <Link to={settingsTab('business')}>Open Settings</Link>}
        </p>
      )}
      {letter === null && error === null && <p className="muted">Loading…</p>}
      {letter && <Letter letter={letter} />}
    </section>
  );
}

function Letter({ letter }: { letter: DonorLetter }) {
  const { business, donor, gifts, year } = letter;
  return (
    <article className="statement" aria-label="Year-End Letter">
      <header className="statement-issuer">
        <h2>{business.legalName}</h2>
        {business.address.map((line) => (
          <p key={line} className="muted">
            {line}
          </p>
        ))}
        <p className="muted">EIN {business.ein}</p>
      </header>

      <div className="letter-to">
        <p>{donor.name}</p>
        {donor.address.map((line) => (
          <p key={line}>{line}</p>
        ))}
      </div>

      <p>Dear {donor.name},</p>
      {gifts.length === 0 ? (
        <p>Our records show no gifts received from you in {year}.</p>
      ) : (
        <>
          <p>Thank you for your support. These are the gifts we received from you in {year}.</p>
          <div className="table-scroll">
            <table className="lines" aria-label="Gifts Received">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Gift</th>
                  <th className="amount">Amount</th>
                  <th className="amount">Goods or Services Provided</th>
                  <th className="amount">Deductible</th>
                </tr>
              </thead>
              <tbody>
                {gifts.map((gift, index) => (
                  <tr key={`${gift.headerId}-${index}`}>
                    <td className="date-cell">{gift.date}</td>
                    <td>
                      {gift.refund ? 'Refund' : gift.pledgePayment ? 'Pledge Payment' : KIND_LABELS[gift.kind]}
                      {gift.kind === 'in_kind' && gift.description && <span className="muted"> · {gift.description}</span>}
                    </td>
                    {/* An in-kind gift is valued by the donor, never here (Publication 1771). */}
                    <td className="amount">{gift.kind === 'in_kind' ? '' : formatCents(gift.amountCents)}</td>
                    <td className="amount">{gift.goodsServicesCents ? formatCents(gift.goodsServicesCents) : ''}</td>
                    <td className="amount">{gift.kind === 'in_kind' ? '' : formatCents(gift.deductibleCents)}</td>
                  </tr>
                ))}
                <tr className="report-total">
                  <td colSpan={2}>Total received</td>
                  <td className="amount">{formatCents(letter.totalReceivedCents)}</td>
                  <td />
                  <td className="amount">{formatCents(letter.totalDeductibleCents)}</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            {letter.anyGoodsServices
              ? 'Except for the goods or services listed above, no goods or services were provided in exchange for these gifts. The deductible amount of a gift is limited to the money given less the value of goods or services received.'
              : 'No goods or services were provided in exchange for these gifts.'}
          </p>
          {gifts.some((gift) => gift.kind === 'in_kind') && (
            <p>Gifts of property are described without a value; their value is for you to determine.</p>
          )}
        </>
      )}
      {business.is501c3 && (
        <p>
          {business.legalName} is exempt from federal income tax under section 501(c)(3). Our EIN is {business.ein}.
        </p>
      )}
      <p>With thanks,</p>
      <p className="signature-line">{business.legalName}</p>

      <p className="muted no-print">
        This wording is a starting point for your CPA to review before letters are sent.
      </p>
    </article>
  );
}
